import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config/api";
import { FaSpinner } from "react-icons/fa";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Share2,
  Play,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

function CoachGalleryads({ coachId }) {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  // focus restore (a11y): remember which tile opened the lightbox
  const openerRef = useRef(null);

  // touch swipe
  const touchStartX = useRef(null);

  // abortable fetch
  const abortRef = useRef(null);

  const cacheKey = useMemo(() => `ptn_gallery_${coachId}`, [coachId]);

  const normalizeArray = (x) => (Array.isArray(x) ? x : x?.$values ?? []);

  const uniqueBy = (arr, key) => {
    const seen = new Set();
    return arr.filter((it) => {
      const k = key(it);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  // ✅ block right-click / drag (best-effort)
  const stopCtx = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  // Load from cache first for instant paint
  useEffect(() => {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data } = JSON.parse(cached);
        if (Array.isArray(data)) {
          setGallery(data);
          setLoading(false);
        }
      } catch {}
    }
  }, [cacheKey]);

  // Fetch fresh
  useEffect(() => {
    setLoadError("");
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    axios
      .get(`${API_BASE}/GalleryMedia/coach/${coachId}`, {
        signal: abortRef.current.signal,
      })
      .then((res) => {
        const raw = normalizeArray(res.data);
        const clean = uniqueBy(
          raw
            .map((m) => ({
              id: m.id ?? m.mediaId ?? m.url ?? Math.random().toString(36).slice(2),
              mediaType:
                String(m.mediaType || "").toLowerCase() === "video" ? "video" : "image",
              url: m.url || m.fileUrl || "",
              // optional: some APIs return thumbnailUrl
              thumb: m.thumbnailUrl || m.thumbUrl || m.url || "",
            }))
            .filter((m) => !!m.url),
          (x) => x.id
        );
        setGallery(clean);
        sessionStorage.setItem(cacheKey, JSON.stringify({ data: clean, t: Date.now() }));
      })
      .catch((err) => {
        if (err?.name === "CanceledError") return;
        setLoadError("Failed to load gallery.");
        if (!gallery.length) setGallery([]);
      })
      .finally(() => setLoading(false));

    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  const openLightbox = (idx, openerEl) => {
    openerRef.current = openerEl || null;
    setCurrent(idx);
    setIsOpen(true);
    setIsZoomed(false);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setIsOpen(false);
    setIsZoomed(false);
    document.body.style.overflow = "";
    openerRef.current?.focus?.();
  };

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + gallery.length) % gallery.length);
    setIsZoomed(false);
  }, [gallery.length]);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % gallery.length);
    setIsZoomed(false);
  }, [gallery.length]);

  // keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  // Preload neighbors for snappy next/prev
  useEffect(() => {
    if (!isOpen || !gallery.length) return;
    const pre = (idx) => {
      const it = gallery[idx];
      if (it && it.mediaType === "image") {
        const img = new Image();
        img.src = it.url;
      }
    };
    pre((current + 1) % gallery.length);
    pre((current - 1 + gallery.length) % gallery.length);
  }, [isOpen, gallery, current]);

  // Touch swipe handlers
  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchMove = () => {};
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? 0) - touchStartX.current;
    if (Math.abs(dx) > 50) {
      dx > 0 ? prev() : next();
    }
    touchStartX.current = null;
  };

  const onShare = async () => {
    try {
      const item = gallery[current];
      if (navigator.share && item?.url) {
        await navigator.share({ title: "PTfinderNow", url: item.url });
      } else if (item?.url) {
        await navigator.clipboard.writeText(item.url);
        // hook toast if you want
      }
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40 col-span-full">
        <FaSpinner className="animate-spin text-3xl text-blue-600" />
      </div>
    );
  }

  if (loadError && !gallery.length) {
    return (
      <div className="col-span-full text-center text-sm text-rose-600">
        {loadError}
      </div>
    );
  }

  return (
    <>
      {/* Tiles (rendered as-is so the PARENT grid controls the layout) */}
      {gallery.length > 0 ? (
        gallery.map((item, index) => {
          const isVideo = item.mediaType === "video";
          return (
            <button
              key={item.id ?? index}
              type="button"
              onContextMenu={stopCtx}
              onClick={(e) => openLightbox(index, e.currentTarget)}
              className="group relative w-full h-48 overflow-hidden rounded-xl shadow-sm bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`Open ${isVideo ? "video" : "image"} ${index + 1}`}
            >
              {/* media */}
              {isVideo ? (
                <video
                  src={item.url}
                  className="h-full w-full object-cover pointer-events-none"
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                />
              ) : (
                <img
                  src={item.thumb || item.url}
                  data-fullsrc={item.url}
                  alt={`Gallery item ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  draggable={false}
                  onDragStart={stopCtx}
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMTAwJScgaGVpZ2h0PScxMDAlJyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnPjxyZWN0IHdpZHRoPScxMDAlJyBoZWlnaHQ9JzEwMCUnIGZpbGw9JyNlZWUnLz48L3N2Zz4=";
                  }}
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                />
              )}

              {/* gradient & overlays */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
              {isVideo && (
                <div className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-xs text-white">
                  <Play className="h-3.5 w-3.5" />
                  Video
                </div>
              )}
            </button>
          );
        })
      ) : (
        <p className="col-span-full text-center text-gray-500">No media yet.</p>
      )}

      {/* Lightbox modal */}
      {isOpen && gallery[current] && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-2 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Media viewer"
          onContextMenu={stopCtx}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-3 right-3 md:top-6 md:right-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Share (no Download) */}
          <div className="absolute left-3 top-3 md:left-6 md:top-6 flex gap-2">
            <button
              onClick={onShare}
              className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label="Share"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>

          {/* Prev */}
          {gallery.length > 1 && (
            <button
              onClick={prev}
              className="absolute left-2 md:left-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label="Previous"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          {/* Media */}
          <div className="w-full max-w-5xl">
            <div
              className={`relative w-full select-none ${
                isZoomed ? "cursor-zoom-out" : "cursor-zoom-in"
              }`}
              onDoubleClick={() => setIsZoomed((z) => !z)}
              onContextMenu={stopCtx}
            >
              {gallery[current].mediaType === "video" ? (
                <video
                  key={gallery[current].url}
                  src={gallery[current].url}
                  controls
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  autoPlay
                  playsInline
                  onContextMenu={stopCtx}
                  className="w-full max-h-[80vh] object-contain rounded-lg shadow-xl bg-black"
                />
              ) : (
                <img
                  key={gallery[current].url}
                  src={gallery[current].url}
                  alt={`Gallery item ${current + 1}`}
                  className={`w-full max-h-[80vh] rounded-lg shadow-xl transition-transform ${
                    isZoomed ? "scale-[1.5]" : "scale-100"
                  } object-contain`}
                  draggable={false}
                  onDragStart={stopCtx}
                  onContextMenu={stopCtx}
                />
              )}

              {/* Zoom hint button (images only) */}
              {gallery[current].mediaType !== "video" && (
                <button
                  type="button"
                  onClick={() => setIsZoomed((z) => !z)}
                  className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-sm text-white"
                >
                  {isZoomed ? (
                    <ZoomOut className="h-4 w-4" />
                  ) : (
                    <ZoomIn className="h-4 w-4" />
                  )}
                  {isZoomed ? "Zoom out" : "Zoom in"}
                </button>
              )}
            </div>

            {/* Counter */}
            <div className="mt-3 text-center text-white/80 text-sm">
              {current + 1} / {gallery.length}
            </div>
          </div>

          {/* Next */}
          {gallery.length > 1 && (
            <button
              onClick={next}
              className="absolute right-2 md:right-6 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
              aria-label="Next"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {/* Click backdrop to close */}
          <button
            type="button"
            aria-hidden="true"
            className="absolute inset-0 -z-10"
            onClick={closeLightbox}
          />
        </div>
      )}
    </>
  );
}

export default CoachGalleryads;
